import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Receipt, Eye, XCircle, Banknote, User, Search,
  FileSearch, BadgeCheck, ArrowRight, ArrowLeft, Shield, Trash2, RotateCcw, Calendar,
  Filter, CalendarDays, Loader2, TrendingUp, BarChart3, PieChart, DollarSign, Hash, Percent, Users2,
  FileText, Send, Download, Phone, Mail, CreditCard, Wallet, Upload, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';

// Status colors
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_review: 'bg-blue-100 text-blue-800',
  pending_authorization: 'bg-purple-100 text-purple-800',
  pending_disbursement: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  retired: 'bg-slate-100 text-slate-600 border-slate-300',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
  returned: 'bg-amber-100 text-amber-900 border-amber-300 ring-1 ring-amber-200 font-semibold',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  pending_authorization: 'Pending Authorization',
  pending_disbursement: 'Pending Approval',
  completed: 'Disbursed',
  retired: 'Retired',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

// Workflow Progress Component
const WorkflowProgress = ({ expense }) => {
  // New flow: 
  // Small amounts (<1M): Submitted → Review → Approval → Complete
  // Large amounts (≥1M): Submitted → Review → Approval → Authorization → Complete
  const stages = expense.requires_authorization
    ? ['Submitted', 'Review', 'Approval', 'Authorization']
    : ['Submitted', 'Review', 'Approval'];
  
  const currentStageMap = {
    'pending_review': 1,
    'pending_disbursement': 2,  // This is now "Approval" stage
    'pending_authorization': 3, // Only for high-value
    'completed': expense.requires_authorization ? 4 : 3,
    'rejected': -1,
    'returned': -2,
  };
  
  const currentIdx = currentStageMap[expense.status] || 0;
  
  if (expense.status === 'rejected') {
    return (
      <div className="flex items-center gap-1 text-xs text-red-600">
        <XCircle className="h-3 w-3" />
        <span>Rejected at {expense.rejected_at_stage || 'workflow'}</span>
      </div>
    );
  }
  
  if (expense.status === 'returned') {
    return (
      <div className="flex items-center gap-1 text-xs text-orange-600">
        <RotateCcw className="h-3 w-3" />
        <span>Returned for changes</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {stages.map((stage, idx) => (
        <React.Fragment key={stage}>
          <div className={`text-xs px-2 py-0.5 rounded ${
            idx < currentIdx ? 'bg-green-100 text-green-700' :
            idx === currentIdx ? 'bg-blue-100 text-blue-700 font-medium' :
            'bg-gray-100 text-gray-500'
          }`}>
            {stage}
          </div>
          {idx < stages.length - 1 && (
            <ArrowRight className={`h-3 w-3 ${idx < currentIdx ? 'text-green-500' : 'text-gray-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Analytics Filter Constants ──────────────────────────────────────
const FILTER_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'this_month', label: 'This Month' },
  { key: 'this_year', label: 'This Year' },
];

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

function getYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= 2020; y--) years.push(String(y));
  return years;
}

const STATUS_BG = {
  pending_review: 'bg-blue-500',
  pending_authorization: 'bg-purple-500',
  pending_disbursement: 'bg-amber-500',
  completed: 'bg-green-500',
  retired: 'bg-orange-500',
  rejected: 'bg-red-500',
  cancelled: 'bg-gray-400',
  returned: 'bg-orange-500',
  draft: 'bg-gray-300',
};

// ─── Expense Analytics Tab Component ────────────────────────────────
function ExpenseAnalyticsTab({ getAuthHeaders, currentOffice }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('this_month');
  const [customDate, setCustomDate] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const [customYear, setCustomYear] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchAnalytics = useCallback(async (filterType, filterValue = null) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      let qs = `filter_type=${filterType}`;
      if (filterValue) qs += `&filter_value=${filterValue}`;
      if (currentOffice?.id) qs += `&office_id=${currentOffice.id}`;

      const res = await fetch(`${API_URL}/api/analytics/expenses?${qs}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.error) {
          console.warn('Analytics computation warning:', data.error);
        }
        setAnalyticsData(data);
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to load expense analytics');
      }
    } catch {
      toast.error('Failed to load expense analytics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, currentOffice, API_URL]);

  useEffect(() => { fetchAnalytics(activeFilter); }, []);

  const handlePreset = (key) => {
    setActiveFilter(key);
    setCustomDate('');
    setCustomMonth('');
    setCustomYear('');
    fetchAnalytics(key);
  };

  const handleDateChange = (val) => {
    setCustomDate(val);
    setActiveFilter('date');
    fetchAnalytics('date', val);
  };

  const handleMonthChange = (val) => {
    setCustomMonth(val);
    setActiveFilter('month');
    fetchAnalytics('month', val);
  };

  const handleYearChange = (val) => {
    setCustomYear(val);
    setActiveFilter('year');
    fetchAnalytics('year', val);
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      handleDateChange(`${y}-${m}-${d}`);
      setCalendarOpen(false);
    }
  };

  const monthYearOptions = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= 2020; y--) {
    const maxMonth = y === now.getFullYear() ? now.getMonth() + 1 : 12;
    for (let m = maxMonth; m >= 1; m--) {
      const val = `${y}-${String(m).padStart(2, '0')}`;
      const label = `${MONTHS[m - 1].label} ${y}`;
      monthYearOptions.push({ value: val, label });
    }
  }

  const selectedDate = customDate ? new Date(customDate + 'T00:00:00') : undefined;
  const dateLabel = customDate
    ? new Date(customDate + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick a date';
  const monthLabel = customMonth
    ? `${MONTHS[parseInt(customMonth.slice(5, 7), 10) - 1]?.label || ''} ${customMonth.slice(0, 4)}`
    : 'Select month';

  const fmtCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  const summary = analyticsData?.summary || {};
  const statusBreakdown = analyticsData?.status_breakdown || {};
  const categoryBreakdown = analyticsData?.category_breakdown || [];
  const departmentBreakdown = analyticsData?.department_breakdown || [];
  const topSpenders = analyticsData?.top_spenders || [];
  const monthlyTrend = analyticsData?.monthly_trend || [];

  // Max for bar width calculations
  const maxCatAmount = Math.max(...categoryBreakdown.map(c => c.amount), 1);
  const maxSpenderAmount = Math.max(...topSpenders.map(s => s.amount), 1);
  const totalStatusCount = Object.values(statusBreakdown).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-5" data-testid="expense-analytics-tab">
      {/* Filter Bar */}
      <Card>
        <CardContent className="py-4 px-5">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-gray-400" />
            {FILTER_PRESETS.map(f => (
              <Button key={f.key} size="sm" data-testid={`expense-analytics-filter-${f.key}`}
                variant={activeFilter === f.key ? 'default' : 'outline'}
                onClick={() => handlePreset(f.key)}
                className={activeFilter === f.key ? 'bg-[#0779BC] hover:bg-[#065d94]' : ''}>
                {f.label}
              </Button>
            ))}
            <div className="h-6 w-px bg-gray-200 mx-1" />

            {/* Date Picker */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="expense-analytics-filter-date"
                  className={`w-[160px] justify-start text-left font-normal ${activeFilter === 'date' ? 'ring-2 ring-[#0779BC] border-[#0779BC]' : ''}`}>
                  <Calendar className="mr-2 h-3.5 w-3.5 text-gray-400" />
                  <span className={`text-xs ${customDate ? 'text-gray-900' : 'text-gray-400'}`}>{dateLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarWidget mode="single" selected={selectedDate} onSelect={handleCalendarSelect} initialFocus />
              </PopoverContent>
            </Popover>

            {/* Month Picker */}
            <Select value={customMonth || ''} onValueChange={handleMonthChange}>
              <SelectTrigger className={`h-8 w-[170px] text-xs ${activeFilter === 'month' ? 'ring-2 ring-[#0779BC] border-[#0779BC]' : ''}`}
                data-testid="expense-analytics-filter-month">
                <CalendarDays className="h-3.5 w-3.5 text-gray-400 mr-1.5 shrink-0" />
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {monthYearOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Year Picker */}
            <Select value={customYear || ''} onValueChange={handleYearChange}>
              <SelectTrigger className={`h-8 w-[90px] text-xs ${activeFilter === 'year' ? 'ring-2 ring-[#0779BC] border-[#0779BC]' : ''}`}
                data-testid="expense-analytics-filter-year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0779BC]" />
          <p className="text-gray-500 mt-3">Loading analytics...</p>
        </div>
      ) : !analyticsData ? (
        <div className="text-center py-16">
          <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No analytics data available</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="expense-analytics-kpis">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <Hash className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{fmtCurrency(summary.total_amount)}</p>
                    <p className="text-xs text-gray-500">Total Requests ({summary.total_requests || 0})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-xl">
                    <BadgeCheck className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-green-700">{fmtCurrency(summary.disbursed_amount)}</p>
                    <p className="text-xs text-gray-500">Disbursed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-xl">
                    <XCircle className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-700">{summary.rejected_count || 0}</p>
                    <p className="text-xs text-gray-500">Rejected</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-xl">
                    <Loader2 className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-700">{fmtCurrency(summary.pending_amount)}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-xl">
                    <TrendingUp className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{fmtCurrency(summary.avg_amount)}</p>
                    <p className="text-xs text-gray-500">Average</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-xl">
                    <Percent className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{summary.approval_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Approval Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Status Breakdown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-gray-500" />
                  Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(statusBreakdown).length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No data</p>
                ) : (
                  Object.entries(statusBreakdown).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-3">
                      <Badge className={`${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'} text-xs min-w-[120px] justify-center`}>
                        {STATUS_LABELS[status] || status}
                      </Badge>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${STATUS_BG[status] || 'bg-gray-400'}`}
                          style={{ width: `${(count / totalStatusCount) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 min-w-[32px] text-right">{count}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-gray-500" />
                  By Category
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {categoryBreakdown.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No data</p>
                ) : (
                  categoryBreakdown.slice(0, 8).map((cat) => (
                    <div key={cat.category} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 font-medium truncate max-w-[140px]">{cat.category}</span>
                        <span className="font-semibold text-gray-900">{fmtCurrency(cat.amount)} <span className="text-xs text-gray-400">({cat.count})</span></span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#0779BC] transition-all"
                          style={{ width: `${(cat.amount / maxCatAmount) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Department Breakdown */}
          {departmentBreakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users2 className="h-4 w-4 text-gray-500" />
                  By Department
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {departmentBreakdown.map((dept) => (
                    <div key={dept.department} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-800 truncate max-w-[160px]">{dept.department}</span>
                        <Badge variant="outline" className="text-xs ml-2 shrink-0">{dept.count} req</Badge>
                      </div>
                      <p className="text-lg font-bold text-gray-900">{fmtCurrency(dept.amount)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-indigo-500 transition-all"
                            style={{ width: `${dept.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 shrink-0">{dept.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Trend */}
          {monthlyTrend.length > 1 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-gray-500" />
                  Monthly Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-40">
                  {(() => {
                    const maxAmt = Math.max(...monthlyTrend.map(m => m.amount), 1);
                    return monthlyTrend.map((m) => (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
                        <span className="text-xs text-gray-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          {fmtCurrency(m.amount)}
                        </span>
                        <div
                          className="w-full max-w-[48px] bg-[#0779BC] rounded-t-md transition-all hover:bg-[#065d94] cursor-default"
                          style={{ height: `${Math.max((m.amount / maxAmt) * 100, 4)}%` }}
                          title={`${m.month}: ${fmtCurrency(m.amount)} (${m.count} requests)`}
                        />
                        <span className="text-[10px] text-gray-400">{m.month.slice(5)}</span>
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Spenders */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                Top Spenders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topSpenders.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-semibold">#</TableHead>
                      <TableHead className="text-xs font-semibold">Employee</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Requests</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Total Amount</TableHead>
                      <TableHead className="text-xs font-semibold w-[30%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topSpenders.slice(0, 10).map((s, idx) => (
                      <TableRow key={s.employee_name}>
                        <TableCell className="text-sm text-gray-500">{idx + 1}</TableCell>
                        <TableCell>
                          <p className="text-sm font-medium text-gray-900">{s.employee_name}</p>
                          {s.office_name && <p className="text-xs text-gray-400">{s.office_name}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-right text-gray-600">{s.count}</TableCell>
                        <TableCell className="text-sm text-right font-semibold text-gray-900">
                          {fmtCurrency(s.amount)}
                        </TableCell>
                        <TableCell>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-400 transition-all"
                              style={{ width: `${(s.amount / maxSpenderAmount) * 100}%` }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}


const ExpenseManagement = () => {
  const { getAuthHeaders, currentOffice, user } = useAuth();
  const [activeMainTab, setActiveMainTab] = useState('approvals');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [processing, setProcessing] = useState(false);

  // Payment Voucher states
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherStep, setVoucherStep] = useState('form'); // 'form' | 'otp' | 'done'
  const [voucherData, setVoucherData] = useState({
    payee_name: '', payee_phone: '', payee_email: '', amount_paid: '',
    purpose: '', payment_mode: 'cash', transfer_channel: '', transfer_account: '',
  });
  const [voucherId, setVoucherId] = useState(null);
  const [voucherNumber, setVoucherNumber] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [voucherProcessing, setVoucherProcessing] = useState(false);
  const [existingVoucher, setExistingVoucher] = useState(null);
  const [existingVouchers, setExistingVouchers] = useState([]);
  const [verifiedTotal, setVerifiedTotal] = useState(0);

  // Retirement states
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [retireStep, setRetireStep] = useState(1); // 1 = amount spent, 2 = voucher reconciliation
  const [retireData, setRetireData] = useState({ amount_spent: '' });
  const [retireReceipt, setRetireReceipt] = useState(null);
  const [retireVouchers, setRetireVouchers] = useState([]);
  const [retireVoucheredTotal, setRetireVoucheredTotal] = useState(0);
  const [retireProcessing, setRetireProcessing] = useState(false);

  // Detail modal voucher list (for viewing vouchers on completed/retired)
  const [detailVouchers, setDetailVouchers] = useState([]);

  // Attachment preview state
  const [previewConfig, setPreviewConfig] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      params.append('page', page);
      params.append('page_size', pageSize);
      
      const response = await fetch(`${API_URL}/api/expenses/admin/all?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
        setSummary(data.summary || {});
        setUserBadges(data.user_badges || []);
        setTotalItems(data.total || 0);
        setTotalPages(data.total_pages || 1);
        const userRoles = user?.roles || [];
        setIsAdmin(userRoles.some(r => r.role === 'super_admin' || r.role === 'office_admin'));
      } else {
        toast.error('Failed to load expense requests');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expense requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [statusFilter, page, pageSize]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchExpenses();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const openDetail = (expense) => {
    setSelectedExpense(expense);
    setDetailVouchers([]);
    setShowDetailModal(true);

    // Fetch vouchers for completed/retired expenses
    if (expense.status === 'completed' || expense.status === 'retired') {
      (async () => {
        try {
          const headers = getAuthHeaders();
          const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
          if (res.ok) {
            const data = await res.json();
            setDetailVouchers((data.vouchers || []).filter(v => v.status === 'verified'));
          }
        } catch {}
      })();
    }
  };

  const openActionModal = (type, expense) => {
    setActionType(type);
    setSelectedExpense(expense);
    setActionNotes('');
    setPaymentRef('');
    setShowActionModal(true);
  };

  const handleAction = async () => {
    if (!selectedExpense) return;
    
    if (actionType === 'reject' && !actionNotes.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    if (actionType === 'send-back' && !actionNotes.trim()) {
      toast.error('Please provide a comment for sending back');
      return;
    }
    
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      let url = `${API_URL}/api/expenses/${selectedExpense.id}`;
      let body = {};
      let method = 'POST';
      
      switch (actionType) {
        case 'review':
          url += '/review';
          body = { notes: actionNotes || null };
          break;
        case 'authorize':
          url += '/authorize';
          body = { notes: actionNotes || null };
          break;
        case 'disburse':
          url += `/disburse${paymentRef ? `?payment_reference=${encodeURIComponent(paymentRef)}` : ''}`;
          body = { notes: actionNotes || null };
          break;
        case 'reject':
          url += '/reject';
          body = { notes: actionNotes };
          break;
        case 'send-back':
          url += '/send-back';
          body = { comment: actionNotes };
          break;
        case 'delete':
          url = `${API_URL}/api/expenses/admin/${selectedExpense.id}`;
          method = 'DELETE';
          body = null;
          break;
        default:
          return;
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      
      if (response.ok) {
        const actionLabels = { 
          review: 'reviewed', 
          authorize: 'authorized', 
          disburse: 'disbursed',
          reject: 'rejected',
          'send-back': 'sent back',
          delete: 'deleted'
        };
        toast.success(`Expense ${actionLabels[actionType]} successfully`);
        setShowActionModal(false);
        setShowDetailModal(false);
        fetchExpenses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // ─── Payment Voucher Handlers ────────────────────
  const openVoucherModal = async (expense) => {
    setSelectedExpense(expense);
    setVoucherStep('form');
    setVoucherData({
      payee_name: '', payee_phone: '', payee_email: '',
      amount_paid: expense.total_amount || '',
      purpose: expense.title || '', payment_mode: 'cash',
      transfer_channel: '', transfer_account: '',
    });
    setOtpValue('');
    setVoucherId(null);
    setVoucherNumber('');
    setExistingVoucher(null);
    setExistingVouchers([]);
    setVerifiedTotal(0);
    setShowVoucherModal(true);

    // Check for existing vouchers
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExistingVouchers(data.vouchers || []);
        setVerifiedTotal(data.verified_total || 0);
        if (data.voucher) {
          setExistingVoucher(data.voucher);
        }
      }
    } catch { /* ignore */ }
  };

  const handleCreateVoucher = async () => {
    if (!voucherData.payee_name || !voucherData.payee_phone || !voucherData.amount_paid || !voucherData.purpose) {
      toast.error('Please fill in all required fields');
      return;
    }
    const amt = parseFloat(voucherData.amount_paid);
    const expenseTotal = selectedExpense?.total_amount || 0;
    const remaining = expenseTotal - verifiedTotal;
    if (amt > expenseTotal) {
      toast.error(`Amount cannot exceed expense total (${expenseTotal.toLocaleString()})`);
      return;
    }
    if (amt > remaining) {
      toast.error(`Amount exceeds remaining balance (${remaining.toLocaleString()}). Already vouchered: ${verifiedTotal.toLocaleString()}`);
      return;
    }
    if (voucherData.payment_mode === 'transfer' && (!voucherData.transfer_channel || !voucherData.transfer_account)) {
      toast.error('Transfer channel and account number are required');
      return;
    }
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const res = await fetch(`${API_URL}/api/payment-vouchers/create`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...voucherData, amount_paid: parseFloat(voucherData.amount_paid), expense_id: selectedExpense.id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVoucherId(data.voucher_id);
        setVoucherNumber(data.voucher_number);
        setVoucherStep('otp');
        toast.success(data.otp_sent ? 'OTP sent to payee phone' : 'Voucher created but OTP delivery failed — try resend');
      } else {
        toast.error(data.detail || 'Failed to create voucher');
      }
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length < 4) { toast.error('Enter the OTP'); return; }
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const res = await fetch(`${API_URL}/api/payment-vouchers/${voucherId}/verify-otp`, {
        method: 'POST', headers,
        body: JSON.stringify({ otp: otpValue }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVoucherStep('done');
        toast.success('Payment voucher verified!');
        fetchExpenses();
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleResendOtp = async () => {
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/${voucherId}/resend-otp`, { method: 'POST', headers });
      const data = await res.json();
      toast[data.success ? 'success' : 'error'](data.message);
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleDownloadVoucherPdf = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/${voucherId}/pdf`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Payment_Voucher_${voucherNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download PDF');
      }
    } catch { toast.error('Download error'); }
  };

  const openRetireModal = async (expense) => {
    setSelectedExpense(expense);
    setRetireStep(1);
    setRetireData({ amount_spent: '' });
    setRetireReceipt(null);
    setRetireVouchers([]);
    setRetireVoucheredTotal(0);
    setShowRetireModal(true);

    // Fetch vouchers for this expense
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRetireVouchers((data.vouchers || []).filter(v => v.status === 'verified'));
        setRetireVoucheredTotal(data.verified_total || 0);
      }
    } catch {}
  };

  const handleRetireExpense = async () => {
    const amtSpent = parseFloat(retireData.amount_spent);
    if (!amtSpent || amtSpent <= 0) {
      toast.error('Please enter a valid amount spent');
      return;
    }
    setRetireProcessing(true);
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const formData = new FormData();
      formData.append('amount_spent', amtSpent.toString());
      if (retireReceipt) formData.append('fiscal_receipt', retireReceipt);

      const res = await fetch(`${API_URL}/api/expenses/${selectedExpense.id}/retire`, {
        method: 'POST', headers, body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Expense retired successfully');
        setShowRetireModal(false);
        setShowDetailModal(false);
        fetchExpenses();
      } else {
        toast.error(data.detail || 'Failed to retire expense');
      }
    } catch { toast.error('Network error'); }
    finally { setRetireProcessing(false); }
  };

  const canPerformAction = (expense, action) => {
    // Check if user can perform the given action based on their badge AND the expense status
    const status = expense.status;
    
    switch (action) {
      case 'review':
        // Only reviewer (or admin) can review, and only when pending_review
        return status === 'pending_review' && (userBadges.includes('reviewer') || isAdmin);
      
      case 'disburse':
        // Only approver (or admin) can approve, and only when pending_disbursement
        return status === 'pending_disbursement' && (userBadges.includes('approver') || isAdmin);
      
      case 'authorize':
        // Only authorizer (or admin) can authorize, and only when pending_authorization
        return status === 'pending_authorization' && (userBadges.includes('authorizer') || isAdmin);
      
      case 'reject':
      case 'send-back':
        // Can reject/send-back only if user has the badge for the CURRENT stage
        if (status === 'pending_review') {
          return userBadges.includes('reviewer') || isAdmin;
        }
        if (status === 'pending_disbursement') {
          return userBadges.includes('approver') || isAdmin;
        }
        if (status === 'pending_authorization') {
          return userBadges.includes('authorizer') || isAdmin;
        }
        return false;
      
      case 'delete':
        // Admin can delete any expense including completed ones
        return isAdmin;
      
      default:
        return false;
    }
  };

  const getNextAction = (expense) => {
    // Get the primary action button based on expense status AND user's badge
    const status = expense.status;
    
    if (status === 'pending_review' && (userBadges.includes('reviewer') || isAdmin)) {
      return { action: 'review', label: 'Review', icon: FileSearch, btnClass: 'bg-blue-600 hover:bg-blue-700 text-white' };
    }
    if (status === 'pending_disbursement' && (userBadges.includes('approver') || isAdmin)) {
      return { action: 'disburse', label: 'Approve', icon: Banknote, btnClass: 'bg-green-600 hover:bg-green-700 text-white' };
    }
    if (status === 'pending_authorization' && (userBadges.includes('authorizer') || isAdmin)) {
      return { action: 'authorize', label: 'Authorize', icon: BadgeCheck, btnClass: 'bg-purple-600 hover:bg-purple-700 text-white' };
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Management</h1>
          <p className="text-gray-500">Manage and analyze expense requests</p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} data-testid="expense-main-tabs">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="approvals" data-testid="expense-tab-approvals" className="data-[state=active]:bg-white">
            <Receipt className="h-4 w-4 mr-2" />
            Approvals
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="expense-tab-analytics" className="data-[state=active]:bg-white">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* ══════ Approvals Tab ══════ */}
        <TabsContent value="approvals" className="mt-4 space-y-6">

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Receipt className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total || 0}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileSearch className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_review || 0}</p>
                <p className="text-xs text-gray-500">Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BadgeCheck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_authorization || 0}</p>
                <p className="text-xs text-gray-500">Auth</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Banknote className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_disbursement || 0}</p>
                <p className="text-xs text-gray-500">Disburse</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Banknote className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.completed || 0}</p>
                <p className="text-xs text-gray-500">Disbursed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.rejected || 0}</p>
                <p className="text-xs text-gray-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by Request ID, Employee, or Title..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending_review', 'pending_authorization', 'pending_disbursement', 'completed', 'retired', 'rejected', 'returned'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setStatusFilter(status); setPage(1); }}
                  className={statusFilter === status ? 'bg-[#0779BC]' : ''}
                >
                  {status === 'all' ? 'All' : STATUS_LABELS[status]}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expense Logs Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Request Logs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading expense requests...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No expense requests found</h3>
              <p className="text-gray-500 mt-1">No requests match your filters</p>
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Request ID</TableHead>
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Title</TableHead>
                  <TableHead className="font-semibold">Amount</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} className="hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600 font-medium">
                        {expense.request_id || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{expense.employee_name}</p>
                          <p className="text-xs text-gray-500">{expense.employee_department}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-900 truncate max-w-[150px]" title={expense.title}>{expense.title}</p>
                      <p className="text-xs text-gray-400">{expense.category}</p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {expense.currency} {formatCurrency(expense.total_amount)}
                        </p>
                        {expense.requires_authorization && (
                          <p className="text-xs text-purple-600 flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            High Value
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">{expense.submitted_at ? new Date(expense.submitted_at).toLocaleDateString() : expense.created_at ? new Date(expense.created_at).toLocaleDateString() : '-'}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[expense.status]}>
                        {STATUS_LABELS[expense.status] || expense.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(expense)}
                        className="gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t mt-4" data-testid="expense-pagination">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border rounded-md px-2 py-1 text-sm bg-white"
                  data-testid="expense-page-size"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="ml-2">
                  Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(1)} data-testid="expense-page-first">
                  First
                </Button>
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)} data-testid="expense-page-prev">
                  Prev
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) { pageNum = i + 1; }
                  else if (page <= 3) { pageNum = i + 1; }
                  else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                  else { pageNum = page - 2 + i; }
                  return (
                    <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(pageNum)}
                      className={page === pageNum ? 'bg-[#155179]' : ''}
                      data-testid={`expense-page-${pageNum}`}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} data-testid="expense-page-next">
                  Next
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)} data-testid="expense-page-last">
                  Last
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        {/* ══════ Analytics Tab ══════ */}
        <TabsContent value="analytics" className="mt-4">
          <ExpenseAnalyticsTab getAuthHeaders={getAuthHeaders} currentOffice={currentOffice} />
        </TabsContent>
      </Tabs>

      {/* Detail Modal with Actions, Workflow, and Details */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedExpense && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Request Details
                  </DialogTitle>
                  <Badge className={STATUS_COLORS[selectedExpense.status]}>
                    {STATUS_LABELS[selectedExpense.status]}
                  </Badge>
                </div>
                <DialogDescription className="flex items-center gap-2 pt-1">
                  <span className="font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">
                    {selectedExpense.request_id || '-'}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Workflow Progress */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-sm font-semibold mb-3 block">Workflow Progress</Label>
                  <WorkflowProgress expense={selectedExpense} />
                </div>

                {/* Request Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Employee</p>
                    <p className="font-medium">{selectedExpense.employee_name}</p>
                    <p className="text-sm text-gray-500">{selectedExpense.employee_department}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-xl font-bold text-blue-600">
                      {selectedExpense.currency} {formatCurrency(selectedExpense.total_amount)}
                    </p>
                    {selectedExpense.requires_authorization && (
                      <p className="text-xs text-purple-600">Requires Authorization (≥ 1M)</p>
                    )}
                  </div>
                </div>

                {/* Title & Description */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Title</p>
                  <p className="font-medium">{selectedExpense.title}</p>
                  {selectedExpense.description && (
                    <p className="text-sm text-gray-600 mt-1">{selectedExpense.description}</p>
                  )}
                </div>

                {/* Purpose */}
                {selectedExpense.purpose && (
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-xs font-medium text-blue-800 mb-1">Business Purpose</p>
                    <p className="text-sm text-blue-700">{selectedExpense.purpose}</p>
                  </div>
                )}

                {/* Items */}
                {selectedExpense.items?.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Expense Items</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Description</TableHead>
                            <TableHead className="text-xs text-right">Qty</TableHead>
                            <TableHead className="text-xs text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedExpense.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{item.description}</TableCell>
                              <TableCell className="text-sm text-right">{item.quantity}</TableCell>
                              <TableCell className="text-sm text-right font-medium">
                                {selectedExpense.currency} {formatCurrency(item.amount * item.quantity)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Approval Trail */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Approval Trail</Label>
                  <div className="space-y-2">
                    {selectedExpense.submitted_at && (
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span>Submitted on {formatDate(selectedExpense.submitted_at)}</span>
                      </div>
                    )}
                    {selectedExpense.reviewed_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm">
                        <FileSearch className="h-4 w-4 text-blue-600" />
                        <span>Reviewed by <strong>{selectedExpense.reviewed_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.reviewed_at)}</span>
                      </div>
                    )}
                    {selectedExpense.authorized_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                        <BadgeCheck className="h-4 w-4 text-purple-600" />
                        <span>Authorized by <strong>{selectedExpense.authorized_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.authorized_at)}</span>
                      </div>
                    )}
                    {selectedExpense.disbursed_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Disbursed by <strong>{selectedExpense.disbursed_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.disbursed_at)}</span>
                      </div>
                    )}
                    {selectedExpense.returned_by_name && (
                      <div className="p-2 bg-orange-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <RotateCcw className="h-4 w-4 text-orange-600" />
                          <span>Returned by <strong>{selectedExpense.returned_by_name}</strong></span>
                          <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.returned_at)}</span>
                        </div>
                        <p className="mt-1 text-orange-700 pl-6">{selectedExpense.return_comment}</p>
                      </div>
                    )}
                    {selectedExpense.rejected_by_name && (
                      <div className="p-2 bg-red-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span>Rejected by <strong>{selectedExpense.rejected_by_name}</strong></span>
                          <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.rejected_at)}</span>
                        </div>
                        <p className="mt-1 text-red-700 pl-6">{selectedExpense.rejection_reason}</p>
                      </div>
                    )}
                    {selectedExpense.payment_reference && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Payment Reference: <strong className="font-mono">{selectedExpense.payment_reference}</strong></span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Vouchers */}
              {(selectedExpense?.status === 'completed' || selectedExpense?.status === 'retired') && detailVouchers.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2 mt-3">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-[#0779BC]" /> Payment Vouchers ({detailVouchers.length})
                  </p>
                  {detailVouchers.map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border">
                      <span className="font-mono text-gray-500">{v.voucher_number}</span>
                      <span className="text-gray-600">{v.payee_name}</span>
                      <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-700 hover:bg-blue-100"
                          onClick={() => setPreviewConfig({
                            title: `Payment Voucher — ${v.voucher_number}`,
                            downloadName: `Payment_Voucher_${v.voucher_number}.pdf`,
                            fetchUrl: `${API_URL}/api/payment-vouchers/${v.id}/pdf`,
                            mime: 'application/pdf',
                          })}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[#0779BC] hover:bg-blue-100"
                          onClick={async () => {
                            try {
                              const headers = getAuthHeaders();
                              const res = await fetch(`${API_URL}/api/payment-vouchers/${v.id}/pdf`, { headers });
                              if (res.ok) {
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Payment_Voucher_${v.voucher_number}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } else { toast.error('Failed to download PDF'); }
                            } catch { toast.error('Download error'); }
                          }}>
                          <Download className="h-3.5 w-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Retirement Info */}
              {selectedExpense?.status === 'retired' && selectedExpense?.retirement && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-2 mt-3">
                  <p className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                    <FileSearch className="h-4 w-4" /> Retirement Summary
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Amount Requested</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.amount_requested || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Amount Spent</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.amount_spent || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Balance Returned</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.balance_returned || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Vouchered</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.vouchered_total || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedExpense.retirement?.fiscal_receipt_url && (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        data-testid="preview-fiscal-receipt-btn"
                        onClick={() => {
                          const receiptUrl = selectedExpense.retirement.fiscal_receipt_url;
                          const ext = receiptUrl.split('.').pop()?.toLowerCase() || 'pdf';
                          setPreviewConfig({
                            title: `Fiscal Receipt — ${selectedExpense.request_id || selectedExpense.id}`,
                            downloadName: `Fiscal_Receipt_${selectedExpense.request_id || selectedExpense.id}.${ext}`,
                            fetchUrl: `${API_URL}${receiptUrl}`,
                          });
                        }}>
                        <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview Receipt
                      </Button>
                      <Button variant="outline" size="sm" className="text-orange-700 border-orange-300 hover:bg-orange-100"
                        data-testid="download-fiscal-receipt-btn"
                        onClick={async () => {
                          try {
                            const hdrs = getAuthHeaders();
                            delete hdrs['Content-Type'];
                            const receiptUrl = selectedExpense.retirement.fiscal_receipt_url;
                            const res = await fetch(`${API_URL}${receiptUrl}`, { headers: hdrs });
                            if (res.ok) {
                              const blob = await res.blob();
                              const ext = receiptUrl.split('.').pop() || 'pdf';
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Fiscal_Receipt_${selectedExpense.request_id || selectedExpense.id}.${ext}`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } else {
                              const errText = await res.text().catch(() => '');
                              console.error('Receipt download failed:', res.status, errText);
                              toast.error(`Failed to download receipt (${res.status})`);
                            }
                          } catch (err) { console.error('Download error:', err); toast.error('Download error: ' + err.message); }
                        }}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download Fiscal Receipt
                      </Button>
                    </div>
                  )}
                  {selectedExpense.retired_by_name && (
                    <p className="text-[10px] text-gray-500">Retired by {selectedExpense.retired_by_name} on {selectedExpense.retired_at?.slice(0,10)}</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <DialogFooter className="flex flex-wrap gap-2 pt-4 border-t">
                {(() => {
                  const nextAction = getNextAction(selectedExpense);
                  const hasActions = nextAction && canPerformAction(selectedExpense, nextAction.action);
                  
                  return (
                    <>
                      {hasActions && (
                        <>
                          <Button 
                            variant="outline"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => openActionModal('send-back', selectedExpense)}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Send Back
                          </Button>
                          <Button 
                            variant="destructive" 
                            onClick={() => openActionModal('reject', selectedExpense)}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button 
                            className={nextAction.btnClass}
                            onClick={() => openActionModal(nextAction.action, selectedExpense)}
                          >
                            <nextAction.icon className="h-4 w-4 mr-2" />
                            {nextAction.label}
                          </Button>
                        </>
                      )}
                      {canPerformAction(selectedExpense, 'delete') && (
                        <Button 
                          variant="ghost"
                          className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => openActionModal('delete', selectedExpense)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      )}
                      {selectedExpense?.status === 'completed' && (
                        <>
                          <Button
                            variant="outline"
                            data-testid="generate-payment-voucher-btn"
                            className="text-[#0779BC] border-[#0779BC] hover:bg-blue-50"
                            onClick={() => { setShowDetailModal(false); openVoucherModal(selectedExpense); }}
                          >
                            <Receipt className="h-4 w-4 mr-2" />
                            Payment Voucher
                          </Button>
                          <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => { setShowDetailModal(false); openRetireModal(selectedExpense); }}
                          >
                            <FileSearch className="h-4 w-4 mr-2" />
                            Retirement
                          </Button>
                        </>
                      )}
                      <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                        Close
                      </Button>
                    </>
                  );
                })()}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'review' && <FileSearch className="h-5 w-5 text-blue-600" />}
              {actionType === 'authorize' && <BadgeCheck className="h-5 w-5 text-purple-600" />}
              {actionType === 'disburse' && <Banknote className="h-5 w-5 text-green-600" />}
              {actionType === 'reject' && <XCircle className="h-5 w-5 text-red-600" />}
              {actionType === 'send-back' && <RotateCcw className="h-5 w-5 text-orange-600" />}
              {actionType === 'delete' && <Trash2 className="h-5 w-5 text-red-600" />}
              {actionType === 'review' && 'Review Expense'}
              {actionType === 'authorize' && 'Authorize Expense (Final)'}
              {actionType === 'disburse' && 'Approve Expense'}
              {actionType === 'reject' && 'Reject Expense'}
              {actionType === 'send-back' && 'Send Back for Changes'}
              {actionType === 'delete' && 'Delete Expense'}
            </DialogTitle>
            <DialogDescription>
              {selectedExpense?.request_id && (
                <span className="font-mono text-blue-600">{selectedExpense.request_id}</span>
              )}
              {selectedExpense?.request_id && ' • '}
              {selectedExpense?.currency} {formatCurrency(selectedExpense?.total_amount || 0)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {actionType === 'review' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800">Next step:</p>
                <p className="text-blue-700">
                  Will proceed to Approver for approval
                  {selectedExpense?.requires_authorization && ' (high-value: will need final authorization after approval)'}
                </p>
              </div>
            )}
            
            {actionType === 'disburse' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <p className="font-medium text-green-800">
                  {selectedExpense?.requires_authorization ? 'Approval - High Value Request' : 'Final Approval'}
                </p>
                <p className="text-green-700">
                  {selectedExpense?.requires_authorization 
                    ? 'After approval, this will proceed to Authorizer for final authorization.'
                    : 'This will complete the expense request.'}
                </p>
              </div>
            )}
            
            {actionType === 'authorize' && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <p className="font-medium text-purple-800">Final Authorization</p>
                <p className="text-purple-700">
                  This is the final step for high-value expenses. Authorization will complete the request.
                </p>
              </div>
            )}
            
            {actionType === 'send-back' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                <p className="font-medium text-orange-800">Send Back:</p>
                <p className="text-orange-700">
                  Request will be returned to submitter for changes.
                </p>
              </div>
            )}
            
            {actionType === 'delete' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="font-medium text-red-800">Warning:</p>
                <p className="text-red-700">
                  This action cannot be undone. The expense request will be permanently deleted.
                </p>
              </div>
            )}
            
            {actionType !== 'delete' && (
              <div className="space-y-2">
                <Label>
                  {actionType === 'reject' ? 'Rejection Reason *' : 
                   actionType === 'send-back' ? 'Comment for Submitter *' : 
                   'Notes (optional)'}
                </Label>
                <Textarea
                  placeholder={
                    actionType === 'reject' ? 'Please provide a reason for rejection...' : 
                    actionType === 'send-back' ? 'Explain what changes are needed...' :
                    'Add any notes...'
                  }
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}
            
            {actionType === 'disburse' && (
              <div className="space-y-2">
                <Label>Payment Reference (optional)</Label>
                <Input
                  placeholder="e.g., TXN-123456"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={processing || (actionType === 'reject' && !actionNotes.trim()) || (actionType === 'send-back' && !actionNotes.trim())}
              className={
                actionType === 'review' ? 'bg-blue-600 hover:bg-blue-700' :
                actionType === 'authorize' ? 'bg-purple-600 hover:bg-purple-700' :
                actionType === 'disburse' ? 'bg-green-600 hover:bg-green-700' :
                actionType === 'send-back' ? 'bg-orange-600 hover:bg-orange-700' :
                'bg-red-600 hover:bg-red-700'
              }
            >
              {processing ? 'Processing...' : (
                actionType === 'review' ? 'Review & Forward' :
                actionType === 'authorize' ? 'Authorize & Complete' :
                actionType === 'disburse' ? 'Approve' :
                actionType === 'send-back' ? 'Send Back' :
                actionType === 'delete' ? 'Delete' :
                'Reject'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[#0779BC]" />
              {voucherStep === 'done' ? 'Payment Voucher' : voucherStep === 'otp' ? 'Verify OTP' : 'Create Payment Voucher'}
            </DialogTitle>
            <DialogDescription>
              {voucherStep === 'form' && 'Enter payee details for the payment voucher. An OTP will be sent to the payee for verification.'}
              {voucherStep === 'otp' && `OTP sent to payee. Enter the code to verify payment acknowledgement.`}
              {voucherStep === 'done' && `Voucher ${voucherNumber} has been verified.`}
            </DialogDescription>
          </DialogHeader>

          {voucherStep === 'form' && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {/* Existing vouchers summary */}
              {existingVouchers.filter(v => v.status === 'verified').length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2">
                  <p className="text-xs font-semibold text-gray-700">Previous Vouchers ({existingVouchers.filter(v => v.status === 'verified').length})</p>
                  {existingVouchers.filter(v => v.status === 'verified').map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-gray-600 bg-white rounded px-2 py-1.5">
                      <span className="font-mono">{v.voucher_number}</span>
                      <span>{v.payee_name}</span>
                      <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      <button
                        data-testid={`voucher-download-${i}`}
                        className="p-1 text-[#0779BC] hover:bg-blue-100 rounded"
                        title="Download PDF"
                        onClick={async () => {
                          try {
                            const headers = getAuthHeaders();
                            const res = await fetch(`${API_URL}/api/payment-vouchers/${v.id}/pdf`, { headers });
                            if (res.ok) {
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Payment_Voucher_${v.voucher_number}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } else { toast.error('Failed to download PDF'); }
                          } catch { toast.error('Download error'); }
                        }}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs pt-1 border-t border-blue-200">
                    <span className="text-gray-500">Total vouchered</span>
                    <span className="font-bold text-gray-800">{selectedExpense?.currency || 'TZS'} {verifiedTotal.toLocaleString()} / {(selectedExpense?.total_amount || 0).toLocaleString()}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Payee Name <span className="text-red-500">*</span></Label>
                  <Input data-testid="voucher-payee-name" placeholder="Full name of payee"
                    value={voucherData.payee_name}
                    onChange={(e) => setVoucherData({ ...voucherData, payee_name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Phone <span className="text-red-500">*</span></Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-2.5 bg-gray-100 border border-r-0 rounded-l-md text-xs text-gray-500">+255</span>
                    <Input data-testid="voucher-payee-phone" placeholder="7XXXXXXXX" className="rounded-l-none"
                      value={voucherData.payee_phone}
                      onChange={(e) => setVoucherData({ ...voucherData, payee_phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email <span className="text-gray-400">(optional)</span></Label>
                  <Input data-testid="voucher-payee-email" placeholder="email@example.com" type="email"
                    value={voucherData.payee_email}
                    onChange={(e) => setVoucherData({ ...voucherData, payee_email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Amount Paid <span className="text-red-500">*</span></Label>
                  <Input data-testid="voucher-amount" type="number" placeholder="0"
                    value={voucherData.amount_paid}
                    onChange={(e) => setVoucherData({ ...voucherData, amount_paid: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Mode of Payment <span className="text-red-500">*</span></Label>
                  <Select value={voucherData.payment_mode} onValueChange={(v) => setVoucherData({ ...voucherData, payment_mode: v })}>
                    <SelectTrigger data-testid="voucher-payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Purpose of Payment <span className="text-red-500">*</span></Label>
                <Textarea data-testid="voucher-purpose" rows={2} placeholder="Describe the purpose..."
                  value={voucherData.purpose}
                  onChange={(e) => setVoucherData({ ...voucherData, purpose: e.target.value })} />
              </div>
              {voucherData.payment_mode === 'transfer' && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div>
                    <Label className="text-xs">Transfer Channel <span className="text-red-500">*</span></Label>
                    <Input data-testid="voucher-transfer-channel" placeholder="e.g. Bank, M-Pesa, Tigo Pesa"
                      value={voucherData.transfer_channel}
                      onChange={(e) => setVoucherData({ ...voucherData, transfer_channel: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Account Number <span className="text-red-500">*</span></Label>
                    <Input data-testid="voucher-transfer-account" placeholder="Account or phone number"
                      value={voucherData.transfer_account}
                      onChange={(e) => setVoucherData({ ...voucherData, transfer_account: e.target.value })} />
                  </div>
                </div>
              )}
              <DialogFooter className="pt-3">
                <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Cancel</Button>
                <Button data-testid="voucher-send-otp-btn" className="bg-[#0779BC] hover:bg-[#065d94]"
                  disabled={voucherProcessing} onClick={handleCreateVoucher}>
                  {voucherProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send OTP & Create
                </Button>
              </DialogFooter>
            </div>
          )}

          {voucherStep === 'otp' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <Phone className="h-8 w-8 text-[#0779BC] mx-auto mb-2" />
                <p className="text-sm text-gray-700">
                  A 6-digit OTP has been sent to the payee's phone.
                </p>
                <p className="text-xs text-gray-500 mt-1">Voucher: <strong>{voucherNumber}</strong> — Valid for 10 minutes</p>
              </div>
              <div>
                <Label className="text-xs">Enter OTP from Payee</Label>
                <Input data-testid="voucher-otp-input" className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6} placeholder="------"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))} />
              </div>
              <DialogFooter className="flex justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={handleResendOtp} disabled={voucherProcessing}>
                  Resend OTP
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Cancel</Button>
                  <Button data-testid="voucher-verify-otp-btn" className="bg-green-600 hover:bg-green-700"
                    disabled={voucherProcessing || otpValue.length < 6} onClick={handleVerifyOtp}>
                    {voucherProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BadgeCheck className="h-4 w-4 mr-2" />}
                    Verify OTP
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}

          {voucherStep === 'done' && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <BadgeCheck className="h-10 w-10 text-green-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-green-800">Voucher Verified</p>
                <p className="text-sm text-gray-600 mt-1">{voucherNumber}</p>
                <p className="text-xs text-gray-400 mt-1">Payee acknowledged payment via OTP</p>
              </div>
              <DialogFooter className="pt-2 flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Close</Button>
                {selectedExpense && (verifiedTotal + parseFloat(voucherData.amount_paid || 0)) < (selectedExpense.total_amount || 0) && (
                  <Button variant="outline" onClick={() => {
                    setVoucherStep('form');
                    setVoucherData({ payee_name: '', payee_phone: '', payee_email: '', amount_paid: '', purpose: '', payment_mode: 'cash', transfer_channel: '', transfer_account: '' });
                    setOtpValue('');
                    setVoucherId(null);
                    setVoucherNumber('');
                    // Refresh voucher list
                    (async () => {
                      try {
                        const headers = getAuthHeaders();
                        const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${selectedExpense.id}`, { headers });
                        if (res.ok) {
                          const data = await res.json();
                          setExistingVouchers(data.vouchers || []);
                          setVerifiedTotal(data.verified_total || 0);
                        }
                      } catch {}
                    })();
                  }}>
                    + Create Another Voucher
                  </Button>
                )}
                <Button data-testid="voucher-download-pdf-btn" className="bg-[#0779BC] hover:bg-[#065d94]" onClick={handleDownloadVoucherPdf}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Retirement Modal */}
      <Dialog open={showRetireModal} onOpenChange={setShowRetireModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-orange-600" />
              Retire Expense
            </DialogTitle>
            <DialogDescription>
              {selectedExpense?.title} — {selectedExpense?.currency || 'TZS'} {(selectedExpense?.total_amount || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-2">
            <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${retireStep === 1 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
              {retireStep > 1 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-[10px]">1</span>}
              Amount Reconciliation
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-gray-300" />
            <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${retireStep === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
              <span className="w-4 h-4 rounded-full bg-gray-300 text-white flex items-center justify-center text-[10px]">2</span>
              Voucher Reconciliation
            </div>
          </div>

          {retireStep === 1 && (() => {
            const requested = selectedExpense?.total_amount || 0;
            const spent = parseFloat(retireData.amount_spent) || 0;
            const balance = requested - spent;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[10px] uppercase text-gray-500 tracking-wide">Amount Requested</p>
                    <p className="text-lg font-bold text-gray-900">{selectedExpense?.currency || 'TZS'} {requested.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <Label className="text-[10px] uppercase text-gray-500 tracking-wide">Amount Spent</Label>
                    <Input
                      data-testid="retire-amount-spent"
                      type="number"
                      placeholder="0"
                      className="mt-1 text-lg font-bold"
                      value={retireData.amount_spent}
                      onChange={(e) => setRetireData({ ...retireData, amount_spent: e.target.value })}
                    />
                  </div>
                </div>
                {spent > 0 && (
                  <div className={`p-3 rounded-lg border ${balance > 0 ? 'bg-green-50 border-green-200' : balance < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Balance</span>
                      <span className={`text-base font-bold ${balance > 0 ? 'text-green-700' : balance < 0 ? 'text-red-700' : 'text-gray-700'}`}>
                        {selectedExpense?.currency || 'TZS'} {Math.abs(balance).toLocaleString()}
                        {balance > 0 ? ' to return' : balance < 0 ? ' overspent' : ' — exact match'}
                      </span>
                    </div>
                  </div>
                )}
                <DialogFooter className="pt-2">
                  <Button variant="outline" onClick={() => setShowRetireModal(false)}>Cancel</Button>
                  <Button
                    data-testid="retire-next-btn"
                    className="bg-orange-600 hover:bg-orange-700"
                    disabled={!spent || spent <= 0}
                    onClick={() => setRetireStep(2)}
                  >
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}

          {retireStep === 2 && (() => {
            const spent = parseFloat(retireData.amount_spent) || 0;
            const unvouchered = spent - retireVoucheredTotal;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2.5 bg-gray-50 rounded-lg border">
                    <p className="text-[10px] uppercase text-gray-500">Amount Spent</p>
                    <p className="text-sm font-bold">{selectedExpense?.currency || 'TZS'} {spent.toLocaleString()}</p>
                  </div>
                  <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[10px] uppercase text-gray-500">Vouchered</p>
                    <p className="text-sm font-bold text-[#0779BC]">{selectedExpense?.currency || 'TZS'} {retireVoucheredTotal.toLocaleString()}</p>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${unvouchered > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <p className="text-[10px] uppercase text-gray-500">Balance</p>
                    <p className={`text-sm font-bold ${unvouchered > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {selectedExpense?.currency || 'TZS'} {Math.abs(unvouchered).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Voucher list */}
                {retireVouchers.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600">Verified Vouchers ({retireVouchers.length})</p>
                    {retireVouchers.map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2.5 py-1.5 border">
                        <span className="font-mono text-gray-500">{v.voucher_number}</span>
                        <span className="text-gray-600">{v.payee_name}</span>
                        <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Fiscal receipt upload when there's unvouchered balance */}
                {unvouchered > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                    <p className="text-xs text-amber-800 font-medium">
                      {selectedExpense?.currency || 'TZS'} {unvouchered.toLocaleString()} is not covered by payment vouchers. Upload fiscal receipt(s).
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-md cursor-pointer hover:bg-amber-50 text-xs font-medium text-amber-700">
                        <Upload className="h-3.5 w-3.5" />
                        {retireReceipt ? retireReceipt.name : 'Choose PDF'}
                        <input
                          data-testid="retire-receipt-upload"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => setRetireReceipt(e.target.files?.[0] || null)}
                        />
                      </label>
                      {retireReceipt && (
                        <button className="text-xs text-red-500 hover:underline" onClick={() => setRetireReceipt(null)}>Remove</button>
                      )}
                    </div>
                  </div>
                )}

                {unvouchered <= 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-700 font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      All spent amounts are fully covered by payment vouchers.
                    </p>
                  </div>
                )}

                <DialogFooter className="pt-2 flex justify-between">
                  <Button variant="outline" onClick={() => setRetireStep(1)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowRetireModal(false)}>Cancel</Button>
                    <Button
                      data-testid="retire-confirm-btn"
                      className="bg-orange-600 hover:bg-orange-700"
                      disabled={retireProcessing || (unvouchered > 0 && !retireReceipt)}
                      onClick={handleRetireExpense}
                    >
                      {retireProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Retire Expense
                    </Button>
                  </div>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <AttachmentPreviewModal
        open={!!previewConfig}
        onClose={() => setPreviewConfig(null)}
        title={previewConfig?.title || 'Attachment'}
        downloadName={previewConfig?.downloadName || 'attachment'}
        fetchUrl={previewConfig?.fetchUrl || ''}
        headers={getAuthHeaders()}
        mime={previewConfig?.mime}
      />
    </motion.div>
  );
};

export default ExpenseManagement;
